import React, { useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff, LockOutlined as LockIcon } from '@mui/icons-material';
import PageLayout from '../../components/layout/PageLayout';
import { PASSWORD_MIN_LENGTH } from '../../components/auth/RegisterForm';
import { apiCall } from '../../utils/fetchAPI';
import { translateApiMessage, translateViolations } from '../../utils/apiMessages';

/**
 * Where the "mot de passe oublié" email lands: choose the new password.
 *
 * It does not sign anybody in. It hands over to the sign-in form, where typing
 * the new password once is also the check that it was typed as meant — which
 * is why there is no second "confirm" field here.
 */
function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [fieldError, setFieldError] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldError('');

    if (password.length < PASSWORD_MIN_LENGTH) {
      setFieldError(`Au moins ${PASSWORD_MIN_LENGTH} caractères`);
      return;
    }

    setSending(true);

    try {
      const response = await apiCall('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, password }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const violations = translateViolations(data.violations);

        if (violations.password) {
          setFieldError(violations.password);
        } else {
          setError(violations.token || translateApiMessage(data.error) || 'Le mot de passe n’a pas pu être changé.');
        }

        return;
      }

      setDone(true);
      setPassword('');
    } catch (err) {
      setError('Le mot de passe n’a pas pu être changé. Réessayez.');
    } finally {
      setSending(false);
    }
  };

  const body = () => {
    if (!token) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Alert severity="error">
            Ce lien est incomplet. Ouvrez-le directement depuis l’email, ou demandez-en un nouveau.
          </Alert>
          <Button component={RouterLink} to="/mot-de-passe-oublie" variant="contained" size="large" fullWidth>
            Demander un nouveau lien
          </Button>
        </Box>
      );
    }

    if (done) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Alert severity="success">Votre mot de passe est changé.</Alert>
          <Button component={RouterLink} to="/login" variant="contained" size="large" fullWidth>
            Se connecter
          </Button>
        </Box>
      );
    }

    return (
      <Box component="form" onSubmit={submit} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <TextField
          label="Nouveau mot de passe"
          type={showPassword ? 'text' : 'password'}
          id="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={Boolean(fieldError)}
          helperText={fieldError || `Au moins ${PASSWORD_MIN_LENGTH} caractères`}
          required
          fullWidth
          disabled={sending}
          autoComplete="new-password"
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={sending}
                  edge="end"
                  title={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {error && (
          <Alert severity="error">
            {error}{' '}
            <Link component={RouterLink} to="/mot-de-passe-oublie">
              Demander un nouveau lien
            </Link>
          </Alert>
        )}

        <Button type="submit" variant="contained" size="large" fullWidth disabled={sending}>
          {sending ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1.5, color: 'inherit' }} />
              Enregistrement…
            </>
          ) : (
            'Changer mon mot de passe'
          )}
        </Button>
      </Box>
    );
  };

  return (
    <PageLayout>
      <Card sx={{ maxWidth: 520, width: '100%', borderRadius: '22px' }}>
        <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
            <Typography variant="h2" component="h1">
              Nouveau mot de passe
            </Typography>
          </Box>
          {body()}
        </CardContent>
      </Card>
    </PageLayout>
  );
}

export default ResetPassword;
